import React, { useEffect, useState, useCallback } from "react";
import { useIrAPadre } from "@/components/BotonVolver";
import { catLabel } from "@/mock/data";
import { Trash2, RotateCcw, FileText, Image as ImageIcon, ChevronLeft, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useData } from "@/context/DataContext";
import { Sheet } from "@/pages/Familia";
import { formatApiErrorDetail } from "@/lib/api";

export default function Papelera() {
  const irA = useIrAPadre();
  const { getPapelera, restaurarDocumento, borrarDocumentoDefinitivo } = useData();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingId, setActingId] = useState(null);
  const [confirmPurge, setConfirmPurge] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setItems(await getPapelera());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar la papelera.");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const restaurar = async (d) => {
    setActingId(d.id);
    try {
      await restaurarDocumento(d.id);
      setItems((prev) => prev.filter((x) => x.id !== d.id));
      toast.success("Documento restaurado", { description: d.filename });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo restaurar.");
    } finally { setActingId(null); }
  };

  const purgar = async () => {
    setBusy(true);
    try {
      await borrarDocumentoDefinitivo(confirmPurge.id);
      setItems((prev) => prev.filter((x) => x.id !== confirmPurge.id));
      toast.success("Documento eliminado definitivamente");
      setConfirmPurge(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar.");
    } finally { setBusy(false); }
  };

  const diasBadge = (n) => {
    if (n == null) return { t: "Sin fecha", c: "text-[#6E7A74] bg-[#F2EDE4]" };
    if (n <= 3) return { t: `${n} día${n === 1 ? "" : "s"} para borrado`, c: "text-[#B91C1C] bg-[#FEE2E2]" };
    if (n <= 10) return { t: `${n} días para borrado`, c: "text-[#B45309] bg-[#FEF3C7]" };
    return { t: `${n} días para borrado`, c: "text-[#2E5645] bg-[#E8F0EC]" };
  };

  return (
    <div className="space-y-5" data-testid="papelera-page">
      <button data-testid="papelera-back" onClick={() => irA("/documentos")}
        className="inline-flex items-center gap-1.5 text-[#3A6B56] font-semibold text-base min-h-[44px]">
        <ChevronLeft size={22} /> Volver a Documentos
      </button>

      <header className="aldia-fade-up">
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-[#1C2421] flex items-center gap-2">
          <Trash2 size={26} className="text-[#3A6B56]" /> Papelera
        </h1>
        <p className="text-base text-[#6E7A74] mt-1">
          Los documentos eliminados se guardan aquí 30 días. Puedes restaurarlos (con sus vínculos) o borrarlos para siempre.
        </p>
      </header>

      {loading ? (
        <div className="py-16 text-center" data-testid="papelera-loading">
          <div className="w-12 h-12 mx-auto rounded-full border-4 border-[#E5DFD5] border-t-[#3A6B56] animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5DFD5] rounded-2xl p-8 text-center" data-testid="papelera-empty">
          <Trash2 size={30} className="text-[#9AA39D] mx-auto mb-2" />
          <p className="text-base text-[#6E7A74]">La papelera está vacía.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="papelera-list">
          {items.map((d) => {
            const Icon = d.type === "Imagen" ? ImageIcon : FileText;
            const badge = diasBadge(d.dias_restantes);
            return (
              <div key={d.id} data-testid={`papelera-item-${d.id}`}
                className="bg-white border border-[#E5DFD5] rounded-2xl p-4">
                <div className="flex items-center gap-4">
                  <div className="shrink-0 w-12 h-12 rounded-xl bg-[#F2EDE4] flex items-center justify-center">
                    <Icon size={24} className="text-[#6E7A74]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-base text-[#1C2421] truncate">{d.filename}</p>
                    <p className="text-sm text-[#6E7A74] truncate">
                      {catLabel(d.category)}{d.deleted_label ? ` · Eliminado el ${d.deleted_label}` : ""}
                    </p>
                    <span className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2.5 py-1 mt-1.5 ${badge.c}`}>
                      {d.dias_restantes != null && d.dias_restantes <= 3 && <AlertTriangle size={12} />} {badge.t}
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`papelera-restore-${d.id}`} onClick={() => restaurar(d)} disabled={actingId === d.id}
                    className="flex-1 min-h-[44px] rounded-xl bg-[#3A6B56] text-white font-semibold text-sm hover:bg-[#2E5645] disabled:opacity-60 flex items-center justify-center gap-1.5">
                    {actingId === d.id ? <Loader2 size={16} className="animate-spin" /> : <RotateCcw size={16} />} Restaurar
                  </button>
                  <button data-testid={`papelera-purge-${d.id}`} onClick={() => setConfirmPurge(d)}
                    className="flex-1 min-h-[44px] rounded-xl bg-[#FEF2F2] text-[#B91C1C] font-semibold text-sm border border-[#FECACA] hover:bg-[#FEE2E2] flex items-center justify-center gap-1.5">
                    <Trash2 size={16} /> Eliminar ya
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {confirmPurge && (
        <Sheet onClose={() => setConfirmPurge(null)} testid="papelera-confirm-purge">
          <h2 className="font-heading text-xl font-bold text-[#1C2421]">¿Eliminar para siempre?</h2>
          <p className="text-base text-[#6E7A74] mt-2">
            «{confirmPurge.filename}» se borrará de forma definitiva y no podrá recuperarse.
          </p>
          <div className="flex gap-2.5 mt-5">
            <button onClick={() => setConfirmPurge(null)} className="flex-1 min-h-[52px] rounded-xl bg-[#F2EDE4] font-semibold text-base border border-[#E5DFD5]">Cancelar</button>
            <button data-testid="papelera-confirm-purge-btn" onClick={purgar} disabled={busy} className="flex-1 min-h-[52px] rounded-xl bg-[#B91C1C] text-white font-semibold text-base disabled:opacity-60">{busy ? "Eliminando…" : "Eliminar ya"}</button>
          </div>
        </Sheet>
      )}
    </div>
  );
}
